import {useCallback} from "react";
import {useReturnTo} from "../../../../hooks/useReturnTo.ts";
import {useSubscription} from "../../subscriptions/hooks/useSubscription.ts";


import {Subscription} from "../../subscriptions/types.ts";
import {useUserAccount} from "../../../../hooks/useUserAccount";

import {determineCustomerType} from '../../../../utils/customerTypeDetection';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';

interface ConfirmSubscriptionFormFields {
    price: string | null;
    quantity: number;
}

export const useConfirmSubscriptionForm = () => {

    const {
        getReturnTo,
        generateRedirectUrl
    } = useReturnTo()

    const navigate = useNavigate()
    const queryClient = useQueryClient();

    const {
        createRecord,
    } = useSubscription();

    const {userAccount} = useUserAccount();

    // Use the same account that's used on the account page
    const account = userAccount;

    // Get product from URL params
    const getProductId = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('productId');
    };

    const product = null; // Product selection removed - use direct subscription creation

    const initialValues: ConfirmSubscriptionFormFields = {
        price: null,
        quantity: 1,
    };

    // Product selection removed - use manual input
    const hasSelectedProduct = false;
    const customerType = determineCustomerType();

    const submitForm = async (values: ConfirmSubscriptionFormFields) => {
        // Always use customer ID from account
        const customerId = account?.customerId;

        if (!customerId) {
            console.error('No customer ID available for subscription creation');
            return;
        }

        // Use manual price input
        const payload = {
            customerId: customerId,
            items: [
                {
                    price: values.price,
                    quantity: values.quantity,
                }
            ]
        };

        try {
            // Create subscription
            await createRecord(payload as unknown as Subscription);

            // Invalidate subscriptions queries
            queryClient.invalidateQueries({queryKey: ['subscriptions']});

            // Redirect
            if (await getReturnTo()) {
                const url = await generateRedirectUrl()
                window.location.assign(url)
            } else {
                navigate('/subscriptions?success=true');
            }
        } catch (e) {
            console.error('Failed to create subscription:', e);
        }
    }

    const handleSubmit = useCallback(
        async (values: ConfirmSubscriptionFormFields) => {
            try {
                await submitForm(values);
            } catch (error) {
                console.error('Form submission error:', error);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return {
        initialValues,
        handleSubmit,
        hasSelectedProduct,
        product,
        getProductId,
        customerType
    };
};